/**
 * The hazard record for an area, and how to read it.
 *
 * Pure: no React, no fetching. The types mirror `api/mete_api/routers/risks.py`.
 *
 * There is deliberately **no single risk score** here. Perils are not
 * commensurable — a commune that floods and one built on shrinking clay have
 * nothing in common but the word "risk" — and a 0–100 number would both hide
 * which one you are buying into and start to resemble the état des risques,
 * which is a regulated document this is not. What the panel gets instead is
 * each peril ranked and named.
 */

export type PerilId = "inondation" | "secheresse" | "mouvement" | "tempete" | "autre";

export const PERIL_LABELS: Record<PerilId, string> = {
  inondation: "Flooding",
  secheresse: "Drought",
  mouvement: "Ground movement",
  tempete: "Storm and hail",
  autre: "Other",
};

/** What each peril actually does to a building, in one line. */
export const PERIL_MEANING: Record<PerilId, string> = {
  inondation:
    "River flooding, mudflow and groundwater rise. Drives building restrictions and insurance friction.",
  secheresse:
    "Subsidence as clay soils shrink in dry summers. The damage is cracked walls, not thirst.",
  mouvement:
    "Landslides, collapse and subsidence — in this region often old gypsum quarries under the northern and eastern suburbs.",
  tempete: "Wind and hail. Rarely decisive on a purchase, and rarely uninsurable.",
  autre: "Declared perils outside the four above.",
};

export interface RiskSummary {
  code: string;
  communes?: number;
  cn_inondation: number;
  cn_secheresse: number;
  cn_mouvement: number;
  cn_tempete: number;
  cn_autre: number;
  cn_total: number;
  cn20_inondation?: number;
  cn20_secheresse?: number;
  cn20_mouvement?: number;
  cn20_tempete?: number;
  cn20_autre?: number;
  cn20_total?: number;
  cn_last_year?: number | null;
  radon?: number | null;
  seismic?: number | null;
  ppr_count?: number;
  ppr_approved?: number;
  ppr_risks?: string | null;
  with_ppr?: number;
}

export interface Declaration {
  peril: PerilId;
  label: string;
  year: number;
  start: string;
}

export interface AreaRisks {
  scope: string;
  code: string;
  resolved_from: string | null;
  exact: boolean;
  summary: RiskSummary;
  declarations: Declaration[];
}

export interface ClayReading {
  niveau: number;
  alea: string;
}

export interface PointRisks {
  lon: number;
  lat: number;
  radius: number;
  clay: ClayReading | null;
  clay_official: { codeExposition: string; exposition: string } | null;
  /** `null` means Géorisques was unreachable — not that there are none. */
  cavities: number | null;
  installations: number | null;
  polluted_sites: number | null;
}

export const CLAY_LABELS: Record<number, string> = {
  1: "Low",
  2: "Medium",
  3: "High",
};

/**
 * Above medium exposure, a land sale requires a geotechnical study and the
 * buyer inherits construction rules. That is the threshold with money attached.
 */
export const CLAY_STUDY_REQUIRED_FROM = 2;

export const RADON_LABELS: Record<number, string> = {
  1: "Low",
  2: "Low, with local geological factors",
  3: "Significant",
};

export const SEISMIC_LABELS: Record<number, string> = {
  1: "Very low",
  2: "Low",
  3: "Moderate",
  4: "Medium",
  5: "Strong",
};

/** The first year GASPAR records — CatNat itself dates from the 1982 law. */
export const CATNAT_FIRST_YEAR = 1982;

export interface PerilRow {
  peril: PerilId;
  label: string;
  total: number;
  recent: number;
  /** Declarations per decade over the last twenty years. */
  perDecade: number;
}

/**
 * The perils that have actually been declared here, worst first.
 *
 * Ranked on the recent count rather than the lifetime one: a commune that
 * flooded eight times before 1995 and has not since is telling you about works
 * that were done, and reading identically to one still flooding would be the
 * wrong answer.
 */
export function rankPerils(summary: RiskSummary): PerilRow[] {
  const perils: PerilId[] = ["inondation", "secheresse", "mouvement", "tempete", "autre"];
  return perils
    .map((peril) => {
      const total = Number(summary[`cn_${peril}` as keyof RiskSummary] ?? 0);
      const recent = Number(summary[`cn20_${peril}` as keyof RiskSummary] ?? 0);
      return {
        peril,
        label: PERIL_LABELS[peril],
        total,
        recent,
        perDecade: recent / 2,
      };
    })
    .filter((row) => row.total > 0)
    .sort((a, b) => b.recent - a.recent || b.total - a.total);
}

/** Declarations per year, for the timeline. Years with none are filled in. */
export function declarationsByYear(
  declarations: Declaration[],
  peril?: PerilId,
): { year: number; n: number }[] {
  const rows = peril ? declarations.filter((d) => d.peril === peril) : declarations;
  const counts = new Map<number, number>();
  for (const d of rows) counts.set(d.year, (counts.get(d.year) ?? 0) + 1);

  const last = new Date().getFullYear();
  const out: { year: number; n: number }[] = [];
  for (let year = CATNAT_FIRST_YEAR; year <= last; year++) {
    out.push({ year, n: counts.get(year) ?? 0 });
  }
  return out;
}

/**
 * How many years since the last declaration, or null if there never was one.
 *
 * The single most legible number in this pane: "last declared 2024" and "last
 * declared 1993" are the same commune on a hazard map and different purchases.
 */
export function yearsSinceLast(summary: RiskSummary): number | null {
  if (!summary.cn_last_year) return null;
  return new Date().getFullYear() - summary.cn_last_year;
}
